import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import {
  selectCashierConfiguration,
  selectPaymentFormPayload,
  selectPaymentFormStatus,
  selectRegistrationRequired,
} from '@cashier/cashier-data-access';
import { FormStatuses, ValidationStatuses } from '@cashier/cashier-model';

interface UseSubmitButton {
  title: string | null;
  isDisabled: boolean;
  isLoading: boolean;
}

export const useSubmitButton = (): UseSubmitButton => {
  const { t, ready: translationsReady } = useTranslation();

  const { orderId, paymentType } = useSelector(selectCashierConfiguration);
  const payload = useSelector(selectPaymentFormPayload);
  const formStatus = useSelector(selectPaymentFormStatus);
  const isRegistrationRequired = useSelector(selectRegistrationRequired);

  const title = useMemo((): string | null => {
    if (!translationsReady) {
      return null;
    }

    const translationMap: { [key: string]: string } = {
      deposit: t('deposit.button'),
      withdrawal: t('withdrawal.button'),
    };

    if (orderId) {
      return t('payment.button');
    }

    if (isRegistrationRequired) {
      return t('Submit');
    }

    return paymentType ? translationMap[paymentType] : null;
  }, [translationsReady, t, orderId, isRegistrationRequired, paymentType]);

  const isDisabled = useMemo((): boolean => {
    if (formStatus !== FormStatuses.READY) {
      return true;
    }

    const isFormValid = Object.keys(payload || {}).every(
      (fieldName) =>
        payload?.[fieldName].validationStatus === ValidationStatuses.SUCCESS,
    );

    return !isFormValid;
  }, [formStatus, payload]);

  return {
    title,
    isDisabled,
    isLoading: formStatus === FormStatuses.SUBMITTED,
  };
};
